import React from "react";
import {
    Box, Grid, Paper, Typography, Chip, Tooltip, IconButton,
    CircularProgress, TablePagination,
} from "@mui/material";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { SECTION_COLORS, REWARD_TYPE_STYLES, ROWS_PER_PAGE } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";

const RewardsTab = ({
    rewardsApi,
    pages,
    handlePageChange,
    onView,
    onEdit,
    onDelete,
    onAdd,
}) => {
    if (rewardsApi.error) return <ErrorState error={rewardsApi.error} onRetry={() => rewardsApi.refetch()} />;
    if (rewardsApi.loading) return <Box py={3} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;

    if (!Array.isArray(rewardsApi.data) || rewardsApi.data.length === 0)
        return <EmptyState message="Aucune récompense configurée" color={SECTION_COLORS.rewards.main} />;

    const page = pages[1];
    const paginated = rewardsApi.data.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

    return (
        <Box>
            <Grid container spacing={2}>
                {paginated.map((r) => {
                    const typeStyle = REWARD_TYPE_STYLES[r.type] || { label: r.type, bgcolor: "#f5f5f5", color: "#555" };
                    return (
                        <Grid item xs={12} sm={6} md={4} key={r.id}>
                            <Paper elevation={0} sx={{
                                p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                                border: `1px solid ${SECTION_COLORS.rewards.main}15`,
                                background: `linear-gradient(135deg, ${SECTION_COLORS.rewards.main}06 0%, transparent 100%)`,
                                transition: "all 0.25s ease",
                                "&:hover": { transform: "translateY(-2px)", boxShadow: `0 8px 25px ${SECTION_COLORS.rewards.main}12`, borderColor: `${SECTION_COLORS.rewards.main}30` },
                            }}>
                                {/* Type badge */}
                                <Tooltip title="Type de récompense" placement="left">
                                    <Chip label={typeStyle.label} size="small" sx={{
                                        position: "absolute", top: 10, right: 10,
                                        fontWeight: 700, fontSize: 10, bgcolor: typeStyle.bgcolor, color: typeStyle.color,
                                    }} />
                                </Tooltip>

                                <Box display="flex" alignItems="center" gap={1} mb={1} mr={8}>
                                    <CardGiftcardIcon sx={{ color: SECTION_COLORS.rewards.main, fontSize: 22 }} />
                                    <Typography fontWeight={700} fontSize={15} fontFamily="Montserrat, sans-serif" color="#1a1a2e" noWrap>
                                        {r.name}
                                    </Typography>
                                </Box>

                                {r.description && (
                                    <Typography fontSize={11} color="text.secondary" mb={1} noWrap sx={{ fontStyle: "italic" }}>
                                        {r.description}
                                    </Typography>
                                )}

                                <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                                    <Chip label={`${r.cost_points != null ? r.cost_points.toLocaleString("fr-FR") : 0} pts`} size="small"
                                        sx={{ fontSize: 11, fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569" }} />
                                    <Chip label={r.validity_days ? `${r.validity_days}j` : "Illimité"} size="small"
                                        sx={{ fontSize: 11, fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569" }} />
                                </Box>

                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <StatusChip active={r.active} />
                                    <Box display="flex" gap={0.5}>
                                        <Tooltip title="Voir le détail">
                                            <IconButton size="small" onClick={() => onView(r)}
                                                sx={{
                                                    color: SECTION_COLORS.rewards.main, bgcolor: `${SECTION_COLORS.rewards.main}10`,
                                                    "&:hover": { bgcolor: `${SECTION_COLORS.rewards.main}20` }, width: 30, height: 30
                                                }}>
                                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Modifier">
                                            <IconButton size="small" onClick={() => onEdit(r)}
                                                sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton size="small" onClick={() => onDelete(r)}
                                                sx={{ color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {rewardsApi.data.length > ROWS_PER_PAGE && (
                <TablePagination component="div" count={rewardsApi.data.length} page={page}
                    onPageChange={handlePageChange(1)} rowsPerPage={ROWS_PER_PAGE} rowsPerPageOptions={[ROWS_PER_PAGE]}
                    sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }} />
            )}
        </Box>
    );
};

export default RewardsTab;
